// ── Jobs Library ──────────────────────────────────────────────────────────────

// Fields to search in requisition-number view
export const REQ_SEARCHABLE_FIELDS = [
  "sb_requisition_number",
  "job_code",
  "job_title_for_jd",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

// Fields to search in job-code view (parent-level keys only)
export const JOB_CODE_SEARCHABLE_FIELDS = [
  "job_code",
  "job_title",
  "business_unit",
  "role_type",
  "job_level",
  "legal_entity",
];

export const FILTER_KEYS = [
  "sb_requisition_number",
  "job_code",
  "job_title",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

export const VIEW_MODES = {
  JOB_CODE:          'job-code',
  REQUISITION_NUMBER: 'requisition-number',
};

// ── JD Status ─────────────────────────────────────────────────────────────────

export const JD_STATUSES = {
  PENDING_ANALYSIS: 'PENDING_ANALYSIS',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PUBLISHED:        'PUBLISHED',
  UNPUBLISHED:      'UNPUBLISHED',
  NEEDS_REVISION:   'NEEDS_REVISION',
  GENERATED:        'GENERATED',
  REGENERATED:      'REGENERATED',
  LOCKED:           'LOCKED',
  NEEDS_WORK:       'NEEDS_WORK',
  GENERATION_FAILED: 'GENERATION_FAILED',
};

// Statuses that allow editing a JD
export const JD_STATUSES_EDITABLE = [
  JD_STATUSES.GENERATED,
  JD_STATUSES.REGENERATED,
  JD_STATUSES.UNPUBLISHED,
];

// PRD §5.1 — tooltip content strings for a JD's status. Shared by Jobs Library
// and Compensation Management so both surfaces describe each status identically.
export const TOOLTIP_STATUS_PROCESSING = "This JD is currently being generated by AI. This typically takes 4-5 minutes. You cannot open it until generation is complete.";
export const TOOLTIP_STATUS_QUEUED = "This JD is waiting or about to start generating. Its status will update automatically.";
export const TOOLTIP_STATUS_UNPUBLISHED = "This JD has been generated but not yet published. It can be edited, revised, or sent for approval.";
export const TOOLTIP_STATUS_PENDING_APPROVAL = "This JD has been submitted for review. The assigned approver will review and either publish or request revisions.";
export const TOOLTIP_STATUS_NEEDS_REVISION = "The approver has requested changes to this JD. The creator needs to make the requested edits and resubmit.";
export const TOOLTIP_STATUS_PUBLISHED = "This JD is live and available for use across SkillsBridge. Download as PDF or DOCX from the Job Details page.";
export const TOOLTIP_STATUS_PROCESSING_FAILED = "JD generation failed. Use the Retry button to try again, or contact your administrator if the issue persists.";

// Maps an underlying jd_status to the appropriate PRD tooltip content.
export const getStatusTooltip = (status) => {
  switch (status) {
    case JD_STATUSES.PUBLISHED:
      return TOOLTIP_STATUS_PUBLISHED;
    case JD_STATUSES.PENDING_APPROVAL:
      return TOOLTIP_STATUS_PENDING_APPROVAL;
    case JD_STATUSES.NEEDS_REVISION:
      return TOOLTIP_STATUS_NEEDS_REVISION;
    case JD_STATUSES.PENDING_ANALYSIS:
      return TOOLTIP_STATUS_PROCESSING;
    case JD_STATUSES.GENERATION_FAILED:
      return TOOLTIP_STATUS_PROCESSING_FAILED;
    case JD_STATUSES.UNPUBLISHED:
    default:
      return TOOLTIP_STATUS_UNPUBLISHED;
  }
};

// A JD sits in PENDING_ANALYSIS from insert until its worker finishes, and a bulk
// upload queues up to 50 at once, drained a few at a time. Worker errors set
// GENERATION_FAILED; only a killed worker leaves a JD stuck here, so treat
// PENDING_ANALYSIS as failed (retryable) only once it is this old.
export const STALE_PENDING_ANALYSIS_MS = 2 * 60 * 60 * 1000;
// A queued JD can wait behind a long queue; only a task that never reached the worker waits this long.
export const STALE_QUEUED_JD_MS = 6 * 60 * 60 * 1000;
export const PENDING_JD_REFRESH_MS = 30 * 1000;

// requisition_view tags each PENDING_ANALYSIS row QUEUED or PROCESSING from the worker's task stream.
export const isJdQueued = (record) =>
  record?.jd_status === JD_STATUSES.PENDING_ANALYSIS && record?.generation_state === 'QUEUED';

export const isJdGenerationFailed = (record, now = Date.now()) => {
  if (record?.jd_status === JD_STATUSES.GENERATION_FAILED) return true;
  if (record?.jd_status !== JD_STATUSES.PENDING_ANALYSIS) return false;
  // Time since the worker's last event, else since the row changed.
  // requisition_view sends epoch ms (number or numeric string); accept an ISO string too.
  // No timestamp means we can't tell, so keep showing it as processing.
  const ts = record.generation_updated_at ?? record.last_updated_time_stamp;
  if (ts === null || ts === undefined || ts === "") return false;
  const updatedAt = Number.isFinite(Number(ts)) ? Number(ts) : Date.parse(ts);
  const staleAfter = isJdQueued(record) ? STALE_QUEUED_JD_MS : STALE_PENDING_ANALYSIS_MS;
  return Number.isFinite(updatedAt) && now - updatedAt > staleAfter;
};

// Green bar fill for a generating JD, from the progress requisition_view sends; 0 means no bar.
export const getJdListProgress = (record) =>
  record?.jd_status !== JD_STATUSES.PENDING_ANALYSIS || isJdQueued(record) || isJdGenerationFailed(record)
    ? 0
    : Number(record?.generation_progress || 0);

// Base status badge display (label/color/tooltip) shared by Jobs Library and
// Compensation Management. Jobs Library layers its own live-task-tracking
// override on top of this (e.g. "Processing Failed" while a JD is generating)
// -- a case Compensation Management's data never includes, since its backing
// query already excludes PENDING_ANALYSIS/GENERATION_FAILED rows.
export const getJdStatusBadge = (status) => ({
  label: status === JD_STATUSES.PUBLISHED ? 'Published' : 'Unpublished',
  color: status === JD_STATUSES.PUBLISHED ? 'success' : 'processing',
  tooltip: getStatusTooltip(status),
});

// Pills for the two in-review states. Colours match the JD status design doc.
// Ant's preset tag colours don't, so these are applied as an inline style.
export const JD_WORKFLOW_STATUS_STYLES = {
  [JD_STATUSES.PENDING_APPROVAL]: {
    label: 'Pending Approval',
    tooltip: TOOLTIP_STATUS_PENDING_APPROVAL,
    style: { color: '#16685f', background: 'rgba(22, 104, 95, 0.1)', borderColor: 'rgba(22, 104, 95, 0.35)' },
  },
  [JD_STATUSES.NEEDS_REVISION]: {
    label: 'Needs Revision',
    tooltip: TOOLTIP_STATUS_NEEDS_REVISION,
    style: { color: '#a2520a', background: 'rgba(162, 82, 10, 0.1)', borderColor: 'rgba(162, 82, 10, 0.35)' },
  },
};

// The in-review status of a list row, or null. requisition_view sends `family_status` (the Mongo
// approval doc's status, always current); `jd_status` is the latest BigQuery version's status and can
// lag, so it is only used for JDs with no approval doc.
export const getJdWorkflowStatus = (record) => {
  // The approval doc is the source of truth when there is one, even if it says the JD is not in review.
  const status = record?.family_status || record?.jd_status;
  return JD_WORKFLOW_STATUS_STYLES[status] ? status : null;
};

// One archived version archives the whole JD family, but requisition_view only drops the archived
// version itself, so an older version of an archived JD can still come back as a row. The approval
// doc's status is ARCHIVED for those; rows without a `family_status` (other views) are kept.
export const dropArchivedJdFamilies = (rows) =>
  Array.isArray(rows) ? rows.filter((row) => row?.family_status !== 'ARCHIVED') : rows;

// Status tag for Jobs Library and Admin All Jobs, with Processing / Queued / Processing Failed
// and the in-review states Pending Approval / Needs Revision.
// Jobs Library passes its own isFailed to hold off red while its live task is still running.
export const getJdStatusDisplay = (record, isFailed = isJdGenerationFailed(record)) => {
  const status = record?.jd_status;
  if (status === JD_STATUSES.PUBLISHED) return getJdStatusBadge(status);
  if (isFailed) return { label: 'Processing Failed', color: 'error', tooltip: TOOLTIP_STATUS_PROCESSING_FAILED };
  if (status === JD_STATUSES.PENDING_ANALYSIS) {
    return isJdQueued(record)
      ? { label: 'Queued', color: 'default', tooltip: TOOLTIP_STATUS_QUEUED }
      : { label: 'Processing', color: 'warning', tooltip: TOOLTIP_STATUS_PROCESSING };
  }
  const workflowStatus = getJdWorkflowStatus(record);
  if (workflowStatus) {
    const { label, tooltip, style } = JD_WORKFLOW_STATUS_STYLES[workflowStatus];
    return { label, color: 'default', tooltip, style };
  }
  return getJdStatusBadge(status);
};

// Status column filter (Ant Table `filters`/`onFilter`) shared by Jobs Library
// and Compensation Management: "Published" matches PUBLISHED exactly;
// "Unpublished" buckets every other jd_status together (GENERATED,
// PENDING_APPROVAL, UNPUBLISHED, HUMAN-EDITED, etc.).
export const JD_STATUS_FILTERS = [
  { text: 'Published', value: JD_STATUSES.PUBLISHED },
  { text: 'Unpublished', value: JD_STATUSES.UNPUBLISHED },
];

export const jdStatusOnFilter = (value, record) =>
  value === JD_STATUSES.PUBLISHED
    ? record.jd_status === JD_STATUSES.PUBLISHED
    : record.jd_status !== JD_STATUSES.PUBLISHED;

// Status column filter for Jobs Library and Admin All Jobs: one option per status tag
// (the 7 labels getJdStatusDisplay can return), in lifecycle order.
export const JD_LIST_STATUS_FILTERS = [
  'Queued',
  'Processing',
  'Processing Failed',
  'Unpublished',
  'Pending Approval',
  'Needs Revision',
  'Published',
].map((label) => ({ text: label, value: label }));

// Matches on the label the row's status tag shows, so each row falls under exactly one option.
export const jdListStatusOnFilter = (value, record) => getJdStatusDisplay(record).label === value;

// ── JD Tabs ───────────────────────────────────────────────────────────────────

export const JD_TABS = {
  JOB_DESCRIPTION: 'job-description',
  JOB_COMPARE:     'job-compare',
};

export const JD_TAB_LABELS = {
  [JD_TABS.JOB_DESCRIPTION]: 'Job Description',
  [JD_TABS.JOB_COMPARE]:     'Compare Published JDs',
};

// ── Human Editor Block Types ───────────────────────────────────────────────────

export const BLOCK_TYPES = {
  LOCKED:     'LOCKED',
  NEEDS_WORK: 'NEEDS_WORK',
  GHOST:      'GHOST',
  MODIFIED:   'MODIFIED',
};

// ── Compare View ──────────────────────────────────────────────────────────────

export const COMPARE_VIEW = {
  DIFFERENCE: 'difference',
  SUMMARY:    'summary',
};

export const COMPARE_VIEW_LABELS = {
  [COMPARE_VIEW.DIFFERENCE]: 'Side-by-side view',
  [COMPARE_VIEW.SUMMARY]:    'AI Comparison Summary',
};

export const COMPARE_ENTRY = {
  JOBS_LIBRARY: 'jobs-library',
  JOB_DETAILS:  'job-details',
};

export const COMPARE_LOADING = {
  AI:      'ai',
  SUMMARY: 'summary',
};

// ── Diff Checker ──────────────────────────────────────────────────────────────

export const VIEW_MODE = {
  LOADING:  'LOADING',
  APPROVER: 'APPROVER',
  HISTORY:  'HISTORY',
};

export const WORKFLOW_STATUS = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  LOADING_STATUS:   'LOADING_STATUS',
};

export const DECISION = {
  APPROVE:  'APPROVE',
  REVIEW:   'REVIEW',
  REMOVED:  'REMOVED',
  MODIFIED: 'MODIFIED',
  UNCHANGED:'UNCHANGED'
};

export const STATUS_CLASS_MAP = {
  added:       'diff-added',
  removed:     'diff-removed',
  modified:    'diff-modified',
  ghost_alert: 'diff-ghost-alert',
};


// ── Feedback ──────────────────────────────────────────────────────────────────

export const FEEDBACK_TYPE = {
    UP: 'WORKS',
    DOWN: 'HAS_ISSUES',
};

// ── JD Creation ───────────────────────────────────────────────────────────────

export const JD_ACTION = { CREATE: 'create', REVISE: 'revise', UPLOAD: 'upload' };

export const FIELD_NAME_MAP = {
  jobCode:            'job_code',
  roleTitle:          'role',
  altRoleTitle:       'alt_role_title',
  jobTitleForJd:      'job_title_for_jd',
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  subFunction:        'sub_function',
  reportingTo:        'reporting_to',
  location:           'location',
  jobGrade:           'level',
  roleType:           'role_type',
  workEnvironment:    'work_environment',
  roleContext:        'role_context',
};

export const FIELD_DB_MAP = {
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  jobGrade:           'level',
  roleType:           'role_type',
  subFunction:        'sub_function',
  location:           'location',
  workEnvironment:    'work_environment',
};
